
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Grid,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Stack,
  useMediaQuery,
  FormControlLabel,
  Switch,
  Tooltip,
  Autocomplete,
  createFilterOptions,
  InputAdornment,
  Collapse,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Popper,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NumbersIcon from "@mui/icons-material/Numbers";
import PersonIcon from "@mui/icons-material/Person";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import StoreIcon from "@mui/icons-material/Store";
import MemoryIcon from "@mui/icons-material/Memory";
import RouteIcon from "@mui/icons-material/Route";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// ✅ Use ONE correct API slice only (update path as per your project)
import {
  useUpdateDeviceMutation,
  useGetDealersQuery,
  useGetDeviceModelsQuery,
  useGetDeviceByIdQuery,
} from "../../redux/admin/deviceApi";
import { useAddDeviceMutation } from "../../redux/admin/adminApi";
import { useGetUsersByRoleQuery } from "../../redux/admin/userApi";
import { useGetAllGroupsQuery } from "../../redux/admin/groupApi"; // if groups api is in admin, keep; else move similarly
import { useGetAllUsersQuery } from "../../redux/admin/dashboardApi";
import { registerCameraNode } from "../../services/cameraV2/cameraV2Service";
import AddUserModal from "../modals/AddUserModal";

// ================== CONFIG ==================
// ✅ Choose which backend route you're using
// "ADMIN" -> POST /devices (your service)
// "USER"  -> POST /users/devices (addMyDevice controller)
const CREATE_MODE = "ADMIN";

// ================== CONSTS ==================
const DEFAULT_VALIDITY_BY_LICENSE_TYPE = {
  monthly: 1,
  quarterly: 3,
  half_yearly: 6,
  yearly: 12,
  lifetime: 1200,
};

const DEFAULT_SINOTRACK_MANUFACTURER_CODE = "TH";

const ICONS = [
  "ambulance",
  "animal",
  "auto",
  "bike",
  "boat",
  "bulldozer",
  "bus",
  "car",
  "cat",
  "crane",
  "delivery_van",
  "dog",
  "dumpher",
  "excavator",
  "female",
  "femalechild",
  "jcb",
  "lorry",
  "luggage",
  "machine",
  "male",
  "malechild",
  "mixer",
  "pickup",
  "police",
  "policebike",
  "policebus",
  "policecar",
  "roadroller",
  "school_bus",
  "scooty",
  "tanker",
  "taxi",
  "tractor",
  "tracktor",
  "trailer",
  "truck",
  "van",
  "custom",
];

const FIELD_TYPES = [
  { label: "Text", value: "text" },
  { label: "Number", value: "number" },
  { label: "Date", value: "date" },
  { label: "Boolean", value: "boolean" },
];

const DEVICE_ATTRIBUTE_DEFS = [
  { key: "speed_limit", fieldName: "Speed Limit(kmhr)", fieldType: "number", defaultValue: "80", payloadKey: "speedLimit" },
  { key: "data_push", fieldName: "Data Push", fieldType: "boolean", defaultValue: "false" },
  { key: "ignition_wire_connected", fieldName: "Ignition wire is connected", fieldType: "boolean", defaultValue: "true" },
  { key: "parking_mode", fieldName: "Parking Mode", fieldType: "boolean", defaultValue: "false" },
  { key: "video", fieldName: "Video", fieldType: "boolean", defaultValue: "false" },
  { key: "timezone", fieldName: "Timezone", fieldType: "text", defaultValue: "" },
  { key: "device_inactivity_start", fieldName: "Device Inactivity Start", fieldType: "text", defaultValue: "" },
  { key: "device_inactivity_period", fieldName: "Device Inactivity Period", fieldType: "text", defaultValue: "" },
  { key: "distance_variation", fieldName: "Distance Variation", fieldType: "text", defaultValue: "" },
  { key: "mileage", fieldName: "Mileage", fieldType: "number", defaultValue: "10" },
  { key: "relay", fieldName: "Relay", fieldType: "boolean", defaultValue: "false" },
  { key: "manufacturer", fieldName: "Manufacturer", fieldType: "text", defaultValue: "" },
  { key: "max_speed_cap", fieldName: "Max Speed Cap(Kmph)", fieldType: "number", defaultValue: "" },
  { key: "model", fieldName: "Model", fieldType: "text", defaultValue: "", payloadKey: "model" },
  { key: "colors", fieldName: "Colors", fieldType: "text", defaultValue: "" },
  { key: "chassis_no", fieldName: "Chasis No", fieldType: "text", defaultValue: "", payloadKey: "chassisNumber" },
  { key: "engine_no", fieldName: "Engine No", fieldType: "text", defaultValue: "", payloadKey: "engineNumber" },
  { key: "over_idle_limit", fieldName: "Over Idle Limit(mins)", fieldType: "number", defaultValue: "" },
  { key: "over_stopped_limit", fieldName: "Over Stopped Limit(mins)", fieldType: "number", defaultValue: "" },
  { key: "remarks", fieldName: "Remarks", fieldType: "text", defaultValue: "" },
];

const DEFAULT_DEVICE_ATTRIBUTE_KEYS = [
  "speed_limit",
  "mileage",
];

function createAttributeField(key) {
  const definition = DEVICE_ATTRIBUTE_DEFS.find((item) => item.key === key);
  if (!definition) return null;
  return {
    key: definition.key,
    fieldName: definition.fieldName,
    fieldType: definition.fieldType,
    required: false,
    fieldValue: definition.defaultValue,
    payloadKey: definition.payloadKey || null,
  };
}

function normalizeCustomFieldKey(field = {}, index = 0) {
  const rawKey = String(field?.key || "").trim().toLowerCase();
  if (rawKey) return rawKey;

  const rawName = String(field?.fieldName || "").trim().toLowerCase();
  const matched = DEVICE_ATTRIBUTE_DEFS.find(
    (item) => item.fieldName.trim().toLowerCase() === rawName
  );
  if (matched?.key) return matched.key;

  return `legacy_${index}`;
}

function normalizeExistingCustomField(field = {}, index = 0) {
  const key = normalizeCustomFieldKey(field, index);
  const definition = DEVICE_ATTRIBUTE_DEFS.find((item) => item.key === key);
  const normalizedType = String(
    field?.fieldType || definition?.fieldType || "text"
  ).toLowerCase();

  return {
    key,
    fieldName: String(field?.fieldName || definition?.fieldName || `Field ${index + 1}`),
    fieldType: normalizedType,
    required: Boolean(field?.required ?? field?.isRequired ?? false),
    fieldValue:
      field?.fieldValue !== undefined && field?.fieldValue !== null
        ? String(field.fieldValue)
        : String(definition?.defaultValue || ""),
    payloadKey: definition?.payloadKey || null,
  };
}

function getCustomFieldValue(device, key) {
  if (!Array.isArray(device?.customFields)) return "";
  const found = device.customFields.find((field, index) => {
    const normalizedKey = normalizeCustomFieldKey(field, index);
    return normalizedKey === key;
  });
  return found?.fieldValue != null ? String(found.fieldValue).trim() : "";
}

function upsertCustomFieldEntry(customFields = [], key, fieldName, fieldValue, fieldType = "text") {
  const nextFields = Array.isArray(customFields) ? [...customFields] : [];
  const existingIndex = nextFields.findIndex((field, index) => normalizeCustomFieldKey(field, index) === key);
  const normalizedValue = fieldValue != null ? String(fieldValue).trim() : "";

  if (!normalizedValue) {
    return existingIndex === -1 ? nextFields : nextFields.filter((_, index) => index !== existingIndex);
  }

  const entry = {
    ...(existingIndex !== -1 ? nextFields[existingIndex] : {}),
    key,
    fieldName,
    fieldType,
    fieldValue: normalizedValue,
  };

  if (existingIndex !== -1) {
    nextFields[existingIndex] = entry;
    return nextFields;
  }

  nextFields.push(entry);
  return nextFields;
}

function normalizeProtocolValue(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeRole(value) {
  return String(value || "").trim().toLowerCase();
}

function resolveEntityId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return String(value?._id || value?.id || value?.value || "").trim();
}

function formatUserOptionLabel(user = {}, fallbackLabel = "User") {
  return (
    String(user?.username || user?.name || user?.fullName || user?.email || fallbackLabel).trim() ||
    fallbackLabel
  );
}

function formatUserOptionSecondary(user = {}) {
  return String(user?.mobileNo || user?.contactNumber || user?.phone || user?.email || "").trim();
}

function getRemainingLicenseCount(user = {}) {
  return (
    Number(
      user?.licenseSummary?.yearlyAvailableLicenses ??
        user?.licenseSummary?.totalRemainingCredits ??
        user?.credits?.balance ??
        user?.remainingTokens ??
        0
    ) || 0
  );
}

function WideAutocompletePopper(props) {
  const incomingWidth = Number(props?.style?.width || 0);
  return (
    <Popper
      {...props}
      placement="bottom-start"
      style={{
        ...props.style,
        width: Math.max(incomingWidth, 360),
      }}
    />
  );
}

const INLINE_ADD_USER_OPTION_VALUE = "__add_new_user__";
const ownerAutocompleteFilter = createFilterOptions({
  stringify: (option) => `${option?.label || ""} ${option?.secondaryLabel || ""}`.trim(),
});
const attributeAutocompleteFilter = createFilterOptions({
  stringify: (option) => `${option?.fieldName || ""} ${option?.key || ""}`.trim(),
});

function resolveProtocolKey(rawValue, protocolOptions = [], modelOptionsByProtocol = {}, modelValue = "") {
  const normalizedRaw = normalizeProtocolValue(rawValue);
  const normalizedModel = normalizeProtocolValue(modelValue);

  if (normalizedRaw) {
    const directMatch = protocolOptions.find((item) => item.key === normalizedRaw);
    if (directMatch) return directMatch.key;

    const labelMatch = protocolOptions.find(
      (item) => normalizeProtocolValue(item.label) === normalizedRaw
    );
    if (labelMatch) return labelMatch.key;

    const modelNamedProtocol = protocolOptions.find((item) =>
      (item.models || []).some((model) => normalizeProtocolValue(model) === normalizedRaw)
    );
    if (modelNamedProtocol) return modelNamedProtocol.key;
  }

  if (normalizedModel) {
    const inferredFromModel = Object.entries(modelOptionsByProtocol).find(([, models]) =>
      (models || []).some((model) => normalizeProtocolValue(model) === normalizedModel)
    );
    if (inferredFromModel?.[0]) return inferredFromModel[0];
  }

  return normalizedRaw;
}

function resolveStoredProtocol(device) {
  return String(
    device?.protocol ||
      device?.protocolName ||
      device?.protocolKey ||
      device?.runtimeProtocol ||
      device?.deviceType ||
      ""
  ).trim();
}

function resolveStoredModel(device) {
  return String(
    device?.model ||
      device?.deviceModel ||
      device?.registeredDeviceModel ||
      getCustomFieldValue(device, "model") ||
      ""
  ).trim();
}

function buildInitialFormFromDevice(device, protocolOptions = [], modelOptionsByProtocol = {}) {
  const existingCustomFields = Array.isArray(device?.customFields)
    ? device.customFields.map((field, index) => normalizeExistingCustomField(field, index))
    : [];

  const ensureField = (key, fallbackValue) => {
    const existingIndex = existingCustomFields.findIndex((field) => field.key === key);
    if (existingIndex !== -1) {
      if (
        (existingCustomFields[existingIndex].fieldValue === "" ||
          existingCustomFields[existingIndex].fieldValue == null) &&
        fallbackValue !== undefined &&
        fallbackValue !== null &&
        fallbackValue !== ""
      ) {
        existingCustomFields[existingIndex] = {
          ...existingCustomFields[existingIndex],
          fieldValue: String(fallbackValue),
        };
      }
      return;
    }

    const next = createAttributeField(key);
    if (!next) return;
    if (fallbackValue !== undefined && fallbackValue !== null && fallbackValue !== "") {
      next.fieldValue = String(fallbackValue);
    }
    existingCustomFields.unshift(next);
  };

  ensureField("mileage", device?.rated_mileage ?? device?.advancedSettings?.mileageTarget ?? 10);
  ensureField(
    "speed_limit",
    device?.advancedSettings?.speedLimit ?? device?.configuration?.overspeedLimit ?? 80
  );
  return {
    ...INITIAL_FORM,
    registrationNo: String(
      device?.vehicleRegistration?.registrationNo ||
        device?.registrationNo ||
        device?.name ||
        ""
    ),
    imei: String(device?.imei || ""),
    simNumber: String(device?.msdn || ""),
    simNumber2: String(getCustomFieldValue(device, "sim_number_2") || ""),
    terminalId: String(device?.terminalId || ""),
    model: resolveStoredModel(device),
    protocolKey:
      resolveProtocolKey(
        resolveStoredProtocol(device),
        protocolOptions,
        modelOptionsByProtocol,
        resolveStoredModel(device)
      ) || "concox",
    protocolField: String(device?.protocol || device?.protocolName ? "protocol" : "deviceType"),
    rawDeviceType: String(device?.deviceType || ""),
    deviceIcon: String(device?.vehicleType || device?.deviceIcon || "car"),
    admin:
      String(
        device?.subAdminOwner?._id ||
          device?.subAdminOwner?.id ||
          device?.dealer?.subAdminOwner?._id ||
          device?.dealer?.subAdminOwner?.id ||
          device?.dealer?.subAdminOwner ||
          device?.assignedToUser?.subAdminOwner?._id ||
          device?.assignedToUser?.subAdminOwner?.id ||
          device?.assignedToUser?.subAdminOwner ||
          ""
      ) || "",
    dealer:
      String(
        device?.dealer?._id ||
          device?.dealer?.id ||
          device?.dealer ||
          ""
      ) || "",
    assignedToUser:
      String(
        device?.assignedToUser?._id ||
          device?.assignedToUser?.id ||
          device?.assignedToUser ||
          ""
      ) || "",
    deviceGroup:
      String(
        device?.deviceGroup?._id ||
          device?.deviceGroup?.id ||
          device?.deviceGroup ||
          ""
      ) || "",
    driverNumber: String(
      device?.driver?.contactNumber ||
        device?.driverContact ||
        ""
    ),
    sinotrackUseImeiAsVehicleId: Boolean(
      device?.sinotrackUseImeiAsVehicleId ??
        (String(device?.sinotrackVehicleId || getCustomFieldValue(device, "sinotrackvehicleid") || getCustomFieldValue(device, "sinotrack_vehicle_id") || "").trim() !== "" &&
        String(device?.sinotrackVehicleId || getCustomFieldValue(device, "sinotrackvehicleid") || getCustomFieldValue(device, "sinotrack_vehicle_id") || "").trim() ===
          String(device?.imei || "").trim())
    ),
    sinotrackVehicleId: String(
      device?.sinotrackVehicleId ||
        getCustomFieldValue(device, "sinotrackvehicleid") ||
        getCustomFieldValue(device, "sinotrack_vehicle_id") ||
        ""
    ),
    sinotrackManufacturerCode: String(
      device?.sinotrackManufacturerCode ||
        getCustomFieldValue(device, "sinotrackmanufacturercode") ||
        getCustomFieldValue(device, "sinotrack_manufacturer_code") ||
        DEFAULT_SINOTRACK_MANUFACTURER_CODE
    ),
    sinotrackDevicePassword: String(
      device?.sinotrackDevicePassword ||
        getCustomFieldValue(device, "sinotrackdevicepassword") ||
        getCustomFieldValue(device, "sinotrack_device_password") ||
        ""
    ),
    todaysOdo: String(device?.todaysOdoRawKm ?? device?.todaysOdo ?? 0),
    licenseType: String(device?.deviceLicense?.licenseType || "yearly"),
    validity: Number(
      device?.deviceLicense?.validity ||
      DEFAULT_VALIDITY_BY_LICENSE_TYPE[String(device?.deviceLicense?.licenseType || "yearly")] ||
      12
    ),
    licenseExpiryDate: formatDateInputValue(
      device?.deviceLicense?.expiryDate ||
      addMonths(
        new Date(),
        DEFAULT_VALIDITY_BY_LICENSE_TYPE[String(device?.deviceLicense?.licenseType || "yearly")] || 12
      )
    ),
    autoRegisterCamera: false,
    cameraNodeName: "",
    pendingAttributeKey: "",
    customFields: existingCustomFields.length
      ? existingCustomFields
      : DEFAULT_DEVICE_ATTRIBUTE_KEYS.map((key) => createAttributeField(key)).filter(Boolean),
  };
}

function createInitialForm() {
  const now = new Date();
  return {
    // Core
    registrationNo: "",
    imei: "",
    simNumber: "",
    simNumber2: "",
    terminalId: "",
    model: "",
    protocolKey: "concox",
    protocolField: "deviceType",
    rawDeviceType: "",
    deviceIcon: "car",
    sinotrackUseImeiAsVehicleId: false,
    sinotrackVehicleId: "",
    sinotrackManufacturerCode: DEFAULT_SINOTRACK_MANUFACTURER_CODE,
    sinotrackDevicePassword: "",

    // Assignment
    admin: "",
    dealer: "",
    assignedToUser: "",
    deviceGroup: "",
    driverNumber: "",
    todaysOdo: "0",

    // License
    licenseType: "yearly",
    validity: DEFAULT_VALIDITY_BY_LICENSE_TYPE.yearly,
    licenseExpiryDate: formatDateInputValue(addMonths(now, DEFAULT_VALIDITY_BY_LICENSE_TYPE.yearly)),
    autoRegisterCamera: false,
    cameraNodeName: "",
    cameraChannelId: 1,
    cameraStreamType: 0,
    cameraDataType: 1,

    // Attribute builder
    customFields: DEFAULT_DEVICE_ATTRIBUTE_KEYS.map((key) => createAttributeField(key)).filter(Boolean),
    pendingAttributeKey: "",
  };
}

const INITIAL_FORM = createInitialForm();

const LICENSE_BOUND_ROLES = ["manager", "office_emp", "sub_admin", "group_admin"];
const OPTIONAL_SELF_LICENSE_ROLES = ["manager", "office_emp", "sub_admin", "group_admin"];

// ================== HELPERS ==================
function addMonths(date, months) {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + Number(months));
  if (d.getDate() !== day) d.setDate(0);
  return d;
}

function formatDateInputValue(date) {
  if (!date) return "";
  const dt = new Date(date);
  if (Number.isNaN(dt.getTime())) return "";
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

function validateIMEI(imei) {
  const s = String(imei || "").trim();
  if (!s) return null;
  if (!/^\d+$/.test(s)) return "IMEI must contain numbers only";
  return null;
}

function validateRegistrationNo(reg) {
  const s = String(reg || "").trim();
  if (!s) return "Registration No is required";
  if (!/^[A-Za-z0-9\- ]{3,20}$/.test(s)) return "Invalid Registration No";
  return null;
}

function validateSimNumber(sim) {
  const s = String(sim || "").trim();
  if (!s) return null; // optional
  if (!/^\d{10,15}$/.test(s)) return "SIM number must be 10 to 15 digits";
  return null;
}

function CollapsibleSection({
  title,
  subtitle,
  icon,
  children,
}) {
  return (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, alignSelf: "stretch" }}>
      <Box
        sx={(t) => ({
          px: 0,
          pt: 0,
          pb: 2,
          borderBottom: `1px solid ${alpha(t.palette.divider, 0.7)}`,
        })}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={(t) => ({
              width: 38,
              height: 38,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              color: t.palette.primary.main,
              bgcolor: alpha(t.palette.primary.main, 0.1),
            })}
          >
            {icon}
          </Box>
          <Box>
            <Typography fontWeight={900} fontSize="1rem" color="text.primary">
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </Box>
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          pt: 2.5,
          "& .register-device-form-grid": {
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            columnGap: 2,
            rowGap: 2,
            width: "100%",
            margin: "0 !important",
          },
          "& .register-device-form-grid > .MuiGrid-item": {
            width: "100% !important",
            maxWidth: "100% !important",
            flexBasis: "auto !important",
            padding: "0 !important",
            minWidth: "0 !important",
          },
          "& .MuiAutocomplete-root": {
            width: "100% !important",
            maxWidth: "100% !important",
            minWidth: "0 !important",
          },
          "& .MuiAutocomplete-root .MuiFormControl-root": {
            width: "100% !important",
          },
          "& .MuiTextField-root": {
            width: "100% !important",
            maxWidth: "100% !important",
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default function AddDeviceModal({
  open,
  onClose,
  onCreated,
  onUpdated,
  userRoleForAssign = "user",
  mode = "create",
  initialDevice = null,
}) {
  const currentUser = useSelector((state) => state?.auth?.user || null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ✅ Dropdowns
  const { data: dealers = [], isLoading: dealersLoading, isError: dealersError } =
    useGetDealersQuery(undefined, { skip: !open });

  const { data: groupsResp, isLoading: groupsLoading, isError: groupsError } =
    useGetAllGroupsQuery({ page: 1, limit: 500 }, { skip: !open });

  const { data: modelsResp, isLoading: modelsLoading, isError: modelsError } =
    useGetDeviceModelsQuery(undefined, { skip: !open });

  const { data: users = [], isLoading: usersLoading, isError: usersError } =
    useGetUsersByRoleQuery(userRoleForAssign, { skip: !open });

  const {
    data: allUsersPayload = [],
    isLoading: allUsersLoading,
    isError: allUsersError,
    refetch: refetchAllUsers,
  } = useGetAllUsersQuery(undefined, { skip: !open });

  const [createDevice, { isLoading: creating }] = useAddDeviceMutation();
  const [updateDevice, { isLoading: updating }] = useUpdateDeviceMutation();

  const [form, setForm] = useState(() => createInitialForm());
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [expandedSections, setExpandedSections] = useState({
    details: true,
    extra: false,
    dataPush: false,
    advanced: false,
  });
  const [activeStep, setActiveStep] = useState(0);
  const [showInlineAddUserModal, setShowInlineAddUserModal] = useState(false);
  const [inlineCreatedUsers, setInlineCreatedUsers] = useState([]);
  const [dealerLicenseDialog, setDealerLicenseDialog] = useState({
    open: false,
    dealerName: "",
  });
  const isEditMode = mode === "edit" && Boolean(initialDevice?._id || initialDevice?.id);
  const activeDeviceId = initialDevice?._id || initialDevice?.id || null;
  const isSubmitting = creating || updating;
  const {
    data: fetchedDevice,
    isFetching: fetchingDeviceDetails,
  } = useGetDeviceByIdQuery(activeDeviceId, {
    skip: !open || !isEditMode || !activeDeviceId,
  });

  const currentUserRole = String(currentUser?.role || "").toLowerCase();
  const requiresDealerLicenseBinding = useMemo(
    () => LICENSE_BOUND_ROLES.includes(currentUserRole),
    [currentUserRole]
  );
  const canUseSelfLicenseCount = useMemo(
    () => OPTIONAL_SELF_LICENSE_ROLES.includes(currentUserRole),
    [currentUserRole]
  );

  const editSourceDevice = fetchedDevice || initialDevice;
  const startDate = useMemo(() => {
    if (isEditMode && editSourceDevice?.deviceLicense?.startDate) {
      return new Date(editSourceDevice.deviceLicense.startDate);
    }
    return new Date();
  }, [isEditMode, editSourceDevice?.deviceLicense?.startDate]);
  const expiryDate = useMemo(() => {
    const selectedDate = form.licenseExpiryDate ? new Date(form.licenseExpiryDate) : null;
    if (selectedDate && !Number.isNaN(selectedDate.getTime())) return selectedDate;
    return addMonths(startDate, Number(form.validity || 1));
  }, [form.licenseExpiryDate, form.validity, startDate]);

  // Normalize groups
  const groups = useMemo(() => {
    if (Array.isArray(groupsResp)) return groupsResp;
    return groupsResp?.data || [];
  }, [groupsResp]);

  const allUsers = useMemo(() => {
    if (Array.isArray(allUsersPayload)) return allUsersPayload;
    if (Array.isArray(allUsersPayload?.users)) return allUsersPayload.users;
    if (Array.isArray(allUsersPayload?.data)) return allUsersPayload.data;
    return [];
  }, [allUsersPayload]);

  const mergedUserPool = useMemo(() => {
    const map = new Map();
    [...allUsers, ...users, ...inlineCreatedUsers].forEach((user) => {
      const id = resolveEntityId(user);
      if (!id) return;
      map.set(id, user);
    });
    return Array.from(map.values());
  }, [allUsers, users, inlineCreatedUsers]);

  const subAdminOptions = useMemo(() => {
    const rows = mergedUserPool
      .filter((user) => normalizeRole(user?.role) === "sub_admin")
      .map((user) => ({
        label: formatUserOptionLabel(user, "Admin"),
        secondaryLabel: formatUserOptionSecondary(user),
        value: resolveEntityId(user),
        meta: user,
      }));

    const existingAdminId = resolveEntityId(
      editSourceDevice?.subAdminOwner ||
        editSourceDevice?.dealer?.subAdminOwner ||
        editSourceDevice?.assignedToUser?.subAdminOwner
    );
    const existingAdmin =
      mergedUserPool.find((user) => resolveEntityId(user) === existingAdminId) ||
      editSourceDevice?.subAdminOwner ||
      editSourceDevice?.dealer?.subAdminOwner ||
      editSourceDevice?.assignedToUser?.subAdminOwner ||
      null;

    if (existingAdminId && !rows.some((row) => String(row.value) === String(existingAdminId))) {
      rows.unshift({
        label: formatUserOptionLabel(existingAdmin || {}, "Admin"),
        secondaryLabel: formatUserOptionSecondary(existingAdmin || {}),
        value: existingAdminId,
        meta: existingAdmin,
      });
    }

    return rows;
  }, [mergedUserPool, editSourceDevice]);

  const protocolOptions = useMemo(() => {
    const raw = Array.isArray(modelsResp?.protocols) ? modelsResp.protocols : [];
    const normalizedApi = raw
      .map((p) => ({
        key: String(p.key || "").trim().toLowerCase(),
        label: String(p.label || p.key || "").trim(),
        models: Array.isArray(p.models) ? p.models : [],
      }))
      .filter((p) => p.key && p.label);

    const merged = new Map();
    normalizedApi.forEach((p) => {
      const key = String(p.key || "").trim().toLowerCase();
      if (!key) return;
      const prev = merged.get(key) || { key, label: p.label || key, models: [] };
      const nextModels = Array.isArray(p.models) ? p.models : [];
      merged.set(key, {
        key,
        label: p.label || prev.label,
        models: Array.from(new Set([...(prev.models || []), ...nextModels].filter(Boolean))),
      });
    });
    return Array.from(merged.values());
  }, [modelsResp]);

  const modelOptionsByProtocol = useMemo(() => {
    const map =
      modelsResp?.modelsByProtocol && typeof modelsResp.modelsByProtocol === "object"
        ? modelsResp.modelsByProtocol
        : {};
    const normalized = {};
    Object.keys(map).forEach((key) => {
      const k = String(key).toLowerCase();
      const incoming = Array.isArray(map[key]) ? map[key] : [];
      normalized[k] = Array.from(new Set([...(normalized[k] || []), ...incoming].filter(Boolean)));
    });
    return normalized;
  }, [modelsResp]);

  const anyLoading =
    dealersLoading || groupsLoading || modelsLoading || usersLoading || allUsersLoading;
  const anyError =
    dealersError || groupsError || modelsError || usersError || allUsersError;

  useEffect(() => {
    if (!open) return;
    if (isEditMode && !editSourceDevice) return;

    setForm(
      isEditMode
        ? buildInitialFormFromDevice(editSourceDevice, protocolOptions, modelOptionsByProtocol)
        : createInitialForm()
    );
    setErrors({});
    setApiError("");
    setExpandedSections({
      details: true,
      extra: false,
      dataPush: false,
      advanced: false,
    });
    setActiveStep(0);
  }, [open, isEditMode, editSourceDevice, protocolOptions, modelOptionsByProtocol]);

  useEffect(() => {
    if (!open) return;
    if (!protocolOptions.length) return;

    setForm((prev) => {
      const protocolRecord = protocolOptions.find(
        (item) => item.key === resolveProtocolKey(prev.protocolKey, protocolOptions, modelOptionsByProtocol, prev.model)
      );
      const resolvedProtocol =
        resolveProtocolKey(prev.protocolKey, protocolOptions, modelOptionsByProtocol, prev.model) ||
        protocolOptions[0]?.key ||
        "concox";
      const allowedModels = modelOptionsByProtocol[resolvedProtocol] || [];
      const fallbackModel = allowedModels.find(Boolean) || protocolRecord?.label || "";
      const matchingModel = allowedModels.find(
        (model) => normalizeProtocolValue(model) === normalizeProtocolValue(prev.model)
      );
      const nextModel =
        prev.model && allowedModels.length === 0
          ? prev.model
          : matchingModel || fallbackModel;

      if (resolvedProtocol === prev.protocolKey && nextModel === prev.model) {
        return prev;
      }

      return { ...prev, protocolKey: resolvedProtocol, model: nextModel };
    });
  }, [open, protocolOptions, modelOptionsByProtocol]);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }, []);

  const setCustomField = (idx, patch) => {
    setForm((prev) => {
      const next = [...prev.customFields];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, customFields: next };
    });
    setErrors((prev) => ({ ...prev, customFields: undefined }));
  };

  const dataPushField = useMemo(
    () =>
      (form.customFields || []).find(
        (field, index) => normalizeCustomFieldKey(field, index) === "data_push"
      ) || null,
    [form.customFields]
  );

  const isDataPushEnabled = dataPushField?.fieldValue === true || dataPushField?.fieldValue === "true";

  const advancedCustomFields = useMemo(
    () =>
      (form.customFields || [])
        .map((field, index) => ({ field, sourceIndex: index }))
        .filter(({ field, sourceIndex }) => normalizeCustomFieldKey(field, sourceIndex) !== "data_push"),
    [form.customFields]
  );

  const availableAttributeOptions = useMemo(
    () =>
      DEVICE_ATTRIBUTE_DEFS.filter(
        (item) =>
          item.key !== "data_push" &&
          !(form.customFields || []).some((field) => field.key === item.key)
      ),
    [form.customFields]
  );

  const setDataPushEnabled = useCallback((checked) => {
    setForm((prev) => {
      const nextCustomFields = checked
        ? upsertCustomFieldEntry(prev.customFields, "data_push", "Data Push", "true", "boolean")
        : (prev.customFields || []).filter(
            (field, index) => normalizeCustomFieldKey(field, index) !== "data_push"
          );

      return {
        ...prev,
        customFields: nextCustomFields,
      };
    });
    setErrors((prev) => ({ ...prev, customFields: undefined }));
  }, []);

  const addCustomRow = () => {
    if (!form.pendingAttributeKey) return;
    const nextField = createAttributeField(form.pendingAttributeKey);
    if (!nextField) return;
    setForm((prev) => ({
      ...prev,
      customFields: [...prev.customFields, nextField],
      pendingAttributeKey: "",
    }));
  };

  const removeCustomRow = (idx) => {
    setForm((prev) => {
      const next = prev.customFields.filter((_, i) => i !== idx);
      return { ...prev, customFields: next.length ? next : INITIAL_FORM.customFields };
    });
  };

  const runValidation = () => {
    const e = {};
    const regErr = validateRegistrationNo(form.registrationNo);
    if (regErr) e.registrationNo = regErr;

    const imeiErr = validateIMEI(form.imei);
    if (imeiErr) e.imei = imeiErr;

    const simErr = validateSimNumber(form.simNumber);
    if (simErr) e.simNumber = simErr;
    const simTwoErr = validateSimNumber(form.simNumber2);
    if (simTwoErr) e.simNumber2 = simTwoErr;

    if (!String(form.protocolKey || "").trim()) e.protocolKey = "Protocol is required";
    if (!String(form.model || "").trim()) e.model = "Model is required";
    if (currentProtocolKey === "sinotrack") {
      if (
        !form.sinotrackUseImeiAsVehicleId &&
        !String(form.sinotrackVehicleId || "").trim()
      ) {
        e.sinotrackVehicleId = "SinoTrack Vehicle ID is required";
      }
    }
    if (!String(form.licenseExpiryDate || "").trim()) {
      e.licenseExpiryDate = "License expiration date is required";
    }
    if (!isEditMode && form.autoRegisterCamera && !String(form.terminalId || "").trim()) {
      e.terminalId = "Camera auto register ke liye Terminal ID required hai";
    }
    if (
      requiresDealerLicenseBinding &&
      !canUseSelfLicenseCount &&
      !form.dealer &&
      !form.assignedToUser
    ) {
      e.dealer = "Dealer selection is required for your role";
    }

    // Custom Fields validation
    const badCustom = (form.customFields || []).some((f) => {
      const name = String(f.fieldName || "").trim();
      const val = String(f.fieldValue || "").trim();
      if (val && !name) return true;
      if (f.required && !val) return true;
      return false;
    });
    if (badCustom) e.customFields = "Custom fields: required fields need a value, and name is needed if value exists.";

    setErrors(e);
    if (Object.keys(e).length) {
      setExpandedSections((prev) => ({
        ...prev,
        details: prev.details || Boolean(e.registrationNo || e.imei || e.dealer),
        extra: prev.extra || Boolean(e.simNumber || e.simNumber2 || e.protocolKey || e.model || e.sinotrackVehicleId || e.terminalId),
        dataPush: prev.dataPush,
        advanced: prev.advanced || Boolean(e.customFields),
      }));
      if (e.registrationNo || e.imei || e.dealer) {
        setActiveStep(0);
      } else if (e.simNumber || e.simNumber2 || e.protocolKey || e.model || e.sinotrackVehicleId || e.terminalId || e.licenseExpiryDate) {
        setActiveStep(1);
      } else {
        setActiveStep(2);
      }
    }
    return Object.keys(e).length === 0;
  };

  const isValidMongoId = (id) => typeof id === "string" && /^[0-9a-fA-F]{24}$/.test(id);

  // ✅ payload builder (ADMIN vs USER)
  const buildPayload = () => {
    let customFields = (form.customFields || [])
      .map((f) => ({
        fieldName: String(f.fieldName || "").trim(),
        fieldValue:
          f.key === "model"
            ? String(form.model || "").trim()
            : f.fieldType === "boolean"
              ? String(f.fieldValue === true || f.fieldValue === "true")
              : String(f.fieldValue || "").trim(),
        fieldType: f.fieldType || "text",
        required: Boolean(f.required),
        key: f.key,
      }))
      .filter((f) => f.fieldName || f.fieldValue);

    customFields = upsertCustomFieldEntry(customFields, "sim_number_2", "Sim Number 2", form.simNumber2, "text");

    const attributeValue = (key) => {
      const attr = (form.customFields || []).find((item) => item.key === key);
      return attr ? attr.fieldValue : undefined;
    };

    const normalizedSpeedLimit = Number(attributeValue("speed_limit") || 80);
    const normalizedMileageTarget = Number(attributeValue("mileage") || 10);
    const resolvedProtocol = String(form.protocolKey || "").trim() || "concox";
    const resolvedModel = String(
      form.model ||
        attributeValue("model") ||
        protocolOptions.find((item) => item.key === resolvedProtocol)?.label ||
        ""
    ).trim();
    const resolvedChassisNumber = String(attributeValue("chassis_no") || "").trim();
    const resolvedEngineNumber = String(attributeValue("engine_no") || "").trim();
    const sinotrackPayload =
      resolvedProtocol === "sinotrack"
        ? {
            sinotrackUseImeiAsVehicleId: Boolean(form.sinotrackUseImeiAsVehicleId),
            sinotrackVehicleId: (
              form.sinotrackUseImeiAsVehicleId
                ? String(form.imei || "").trim()
                : String(form.sinotrackVehicleId || "").trim()
            ),
            sinotrackManufacturerCode:
              String(form.sinotrackManufacturerCode || "").trim().toUpperCase() ||
              DEFAULT_SINOTRACK_MANUFACTURER_CODE,
            sinotrackDevicePassword: String(form.sinotrackDevicePassword || "").trim() || undefined,
          }
        : {};

    if (isEditMode) {
      const protocolPayload =
        form.protocolField === "protocol"
          ? {
              protocol: resolvedProtocol,
              deviceType: String(form.rawDeviceType || editSourceDevice?.deviceType || "").trim(),
            }
          : { deviceType: resolvedProtocol };

      return {
        name: String(form.registrationNo || "").trim().toUpperCase(),
        deviceId:
          String(editSourceDevice?.deviceId || "").trim() ||
          String(form.registrationNo || "").trim().toUpperCase(),
        registrationNo: String(form.registrationNo || "").trim().toUpperCase(),
        "vehicleRegistration.registrationNo": String(form.registrationNo || "").trim().toUpperCase(),
        imei: String(form.imei || "").trim(),
        msdn: form.simNumber ? String(form.simNumber).trim() : null,
        todaysOdo: Number(form.todaysOdo || 0),
        terminalId: form.terminalId ? String(form.terminalId).trim() : null,
        ...protocolPayload,
        model: resolvedModel,
        deviceModel: resolvedModel,
        ...sinotrackPayload,
        dealer: form.dealer || null,
        assignedToUser: form.assignedToUser || null,
        deviceGroup: form.deviceGroup || null,
        vehicleType: form.deviceIcon || "car",
        deviceIcon: form.deviceIcon || "car",
        "driver.contactNumber": form.driverNumber ? String(form.driverNumber).trim() : "",
        "vehicleRegistration.chassisNumber": resolvedChassisNumber || "",
        "vehicleRegistration.engineNumber": resolvedEngineNumber || "",
        "deviceLicense.licenseType": form.licenseType,
        "deviceLicense.validity": Number(form.validity),
        "deviceLicense.startDate": startDate,
        "deviceLicense.expiryDate": expiryDate,
        "deviceLicense.isActive": expiryDate.getTime() >= Date.now(),
        "advancedSettings.speedLimit": normalizedSpeedLimit,
        "advancedSettings.mileageTarget": normalizedMileageTarget,
        rated_mileage: normalizedMileageTarget,
        customFields,
      };
    }

    if (CREATE_MODE === "USER") {
      // matches addMyDevice controller
      return {
        deviceId: String(form.registrationNo || "").trim().toUpperCase(), // using reg as deviceId
        name: String(form.registrationNo || "").trim().toUpperCase(),
        imei: String(form.imei || "").trim(),
        msdn: form.simNumber ? String(form.simNumber).trim() : null,
        protocol: resolvedProtocol,
        deviceType: resolvedProtocol,
        model: resolvedModel,
        ...sinotrackPayload,
        registrationNo: String(form.registrationNo || "").trim().toUpperCase(),
        driverContact: form.driverNumber ? String(form.driverNumber).trim() : undefined,
        chassisNumber: resolvedChassisNumber || undefined,
        engineNumber: resolvedEngineNumber || undefined,
        // optional: geoLocation/geofences can be added later
        customFields,
      };
    }

    // ADMIN mode payload aligned with old working create flow
    return {
      deviceId:
        String(form.imei || "").trim() ||
        String(form.registrationNo || "").trim().toUpperCase(),
      name: String(form.registrationNo || "").trim().toUpperCase(),
      registrationNo: String(form.registrationNo || "").trim().toUpperCase(),
      model: resolvedModel,
      imei: String(form.imei || "").trim(),
      msdn: form.simNumber ? String(form.simNumber).trim() : "",
      dealer: isValidMongoId(form.dealer) ? form.dealer : undefined,
      assignedToUser: isValidMongoId(form.assignedToUser) ? form.assignedToUser : undefined,
      deviceType: "hardwire",
      vehicleType: form.deviceIcon || "car",
      protocol: resolvedProtocol || undefined,
      ...sinotrackPayload,
      terminalId: form.terminalId ? String(form.terminalId).trim() : undefined,
      driverContact: form.driverNumber ? String(form.driverNumber).trim() : undefined,
      customFields,
      speedLimit: normalizedSpeedLimit,
      mileageTarget: normalizedMileageTarget,
      rated_mileage: normalizedMileageTarget,
      licenseType: form.licenseType,
      validity: Number(form.validity),
      licenseExpiryDate: expiryDate,
      ...(form.deviceGroup ? { deviceGroup: form.deviceGroup } : {}),
      ...(resolvedChassisNumber ? { chassisNumber: resolvedChassisNumber } : {}),
      ...(resolvedEngineNumber ? { engineNumber: resolvedEngineNumber } : {}),
    };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    if (!runValidation()) return;

    try {
      const payload = buildPayload();
      const res = isEditMode
        ? await updateDevice({ id: activeDeviceId, ...payload }).unwrap()
        : await createDevice(payload).unwrap();

      const createdDevice = res?.device || res?.data?.device || res?.data || null;
      const createdDeviceId = createdDevice?._id || createdDevice?.id || null;

      let cameraNode = null;
      let cameraNodeWarning = null;

      if (!isEditMode && form.autoRegisterCamera) {
        if (!createdDeviceId) {
          cameraNodeWarning = "Device bana, lekin camera node ke liye deviceId response me nahi mila.";
        } else {
          const cameraPayload = {
            deviceId: String(createdDeviceId),
            name:
              String(form.cameraNodeName || "").trim() ||
              `${String(form.registrationNo || "Camera").trim()} Camera`,
            mode: "jt808",
            channelId: Number(form.cameraChannelId) || 1,
            streamType: Number(form.cameraStreamType),
            dataType: Number(form.cameraDataType),
          };

          if (String(form.terminalId || "").trim()) {
            cameraPayload.terminalId = String(form.terminalId).trim();
          }

          try {
            cameraNode = await registerCameraNode(cameraPayload);
          } catch (cameraErr) {
            cameraNodeWarning =
              cameraErr?.response?.data?.msg ||
              cameraErr?.response?.data?.error ||
              cameraErr?.message ||
              "Camera node registration failed";
          }
        }
      }

      if (cameraNodeWarning) {
        window.alert(
          `Device register ho gaya, lekin camera node auto-register nahi hua.\nReason: ${cameraNodeWarning}\n\nAap /camera-portal se manually register kar sakte ho.`
        );
      }

      window.dispatchEvent(new Event("app:crud-changed"));

      if (isEditMode) {
        onUpdated?.({ ...res, cameraNode, cameraNodeWarning });
      } else {
        onCreated?.({ ...res, cameraNode, cameraNodeWarning });
      }
      onClose?.();
    } catch (err) {
      const backendMsg = err?.data?.msg || err?.data?.error || err?.error || err?.message;
      const license = err?.data?.license;
      const extra = [];
      if (license?.warningMessage) extra.push(license.warningMessage);
      if (typeof license?.totalRemainingCredits === "number") {
        extra.push(`Remaining license count: ${license.totalRemainingCredits}`);
      }
      const detail = extra.length ? ` (${extra.join(" | ")})` : "";
      setApiError((backendMsg || "Failed to create device") + detail);
    }
  };

  // options for autocomplete
  const selectedAdminId = resolveEntityId(form.admin);

  const dealerOptions = useMemo(() => {
    const dealerMap = new Map();

    dealers.forEach((dealer) => {
      const dealerId = resolveEntityId(dealer);
      if (!dealerId) return;
      dealerMap.set(dealerId, dealer);
    });

    mergedUserPool
      .filter((user) => normalizeRole(user?.role) === "group_admin")
      .forEach((dealer) => {
        const dealerId = resolveEntityId(dealer);
        if (!dealerId) return;
        const existing = dealerMap.get(dealerId) || {};
        dealerMap.set(dealerId, { ...existing, ...dealer });
      });

    const rows = Array.from(dealerMap.values())
      .map((dealer) => ({
        label: formatUserOptionLabel(dealer, "Dealer"),
        secondaryLabel: formatUserOptionSecondary(dealer),
        value: resolveEntityId(dealer),
        meta: dealer,
        remainingLicenses: getRemainingLicenseCount(dealer),
        hasKnownLicenseCount:
          dealer?.licenseSummary?.yearlyAvailableLicenses != null ||
          dealer?.licenseSummary?.totalRemainingCredits != null ||
          dealer?.remainingTokens != null ||
          dealer?.credits?.balance != null,
      }))
      .filter((dealer) => {
        if (!selectedAdminId) return true;
        const subAdminOwnerId = resolveEntityId(dealer.meta?.subAdminOwner);
        const createdById = resolveEntityId(dealer.meta?.createdBy);
        return (
          String(subAdminOwnerId) === String(selectedAdminId) ||
          String(createdById) === String(selectedAdminId)
        );
      });

    const existingDealer = editSourceDevice?.dealer;
    const existingId = existingDealer?._id || existingDealer?.id || existingDealer;
    const existingLabel = formatUserOptionLabel(existingDealer || {}, existingId || "Dealer");
    if (existingId && !rows.some((row) => String(row.value) === String(existingId))) {
      rows.unshift({
        label: existingLabel || "Dealer",
        secondaryLabel: formatUserOptionSecondary(existingDealer || {}),
        value: existingId,
        meta: existingDealer,
        remainingLicenses: getRemainingLicenseCount(existingDealer || {}),
        hasKnownLicenseCount: true,
      });
    }
    return rows;
  }, [dealers, mergedUserPool, editSourceDevice, selectedAdminId]);

  const scopedUsers = useMemo(() => {
    const roleFilteredUsers = mergedUserPool.filter(
      (user) => normalizeRole(user?.role) === normalizeRole(userRoleForAssign)
    );
    const selectedDealerId = resolveEntityId(form.dealer);
    const visibleDealerIds = new Set(dealerOptions.map((dealer) => String(dealer.value)));

    if (selectedDealerId) {
      return roleFilteredUsers.filter(
        (user) => resolveEntityId(user?.dealerOwner) === selectedDealerId
      );
    }

    if (!selectedAdminId) return roleFilteredUsers;

    return roleFilteredUsers.filter((user) => {
      const dealerOwnerId = resolveEntityId(user?.dealerOwner);
      const subAdminOwnerId = resolveEntityId(user?.subAdminOwner);
      const createdById = resolveEntityId(user?.createdBy);

      return (
        String(subAdminOwnerId) === String(selectedAdminId) ||
        String(createdById) === String(selectedAdminId) ||
        visibleDealerIds.has(String(dealerOwnerId))
      );
    });
  }, [mergedUserPool, userRoleForAssign, form.dealer, selectedAdminId, dealerOptions]);

  const scopedUserIds = useMemo(
    () => new Set(scopedUsers.map((user) => String(resolveEntityId(user?._id || user)))),
    [scopedUsers]
  );

  const userOptions = useMemo(() => {
    const rows = scopedUsers.map((u) => ({
      label: formatUserOptionLabel(u, "User"),
      secondaryLabel: formatUserOptionSecondary(u),
      value: u._id,
      meta: u,
    }));
    const existingUser = editSourceDevice?.assignedToUser;
    const existingId = existingUser?._id || existingUser?.id || existingUser;
    const existingLabel = formatUserOptionLabel(existingUser || {}, existingId || "User");
    if (
      existingId &&
      !rows.some((row) => String(row.value) === String(existingId))
    ) {
      rows.unshift({
        label: existingLabel || "User",
        secondaryLabel: formatUserOptionSecondary(existingUser || {}),
        value: existingId,
        meta: existingUser,
      });
    }
    return rows;
  }, [scopedUsers, editSourceDevice]);
  const groupOptions = useMemo(() => {
    const rows = groups.map((g) => ({ label: g.name || "Group", value: g._id }));
    const existingGroup = editSourceDevice?.deviceGroup;
    const existingId = existingGroup?._id || existingGroup?.id || existingGroup;
    const existingLabel = existingGroup?.name || existingId;
    if (
      existingId &&
      !rows.some((row) => String(row.value) === String(existingId))
    ) {
      rows.unshift({ label: existingLabel || "Group", value: existingId });
    }
    return rows;
  }, [groups, editSourceDevice]);
  const currentProtocolKey = useMemo(
    () =>
      resolveProtocolKey(form.protocolKey, protocolOptions, modelOptionsByProtocol, form.model) ||
      String(form.protocolKey || "").trim().toLowerCase(),
    [form.protocolKey, form.model, protocolOptions, modelOptionsByProtocol]
  );
  const protocolModelOptionsList = useMemo(() => {
    const rowsByModel = new Map();

    protocolOptions.forEach((protocol) => {
      (modelOptionsByProtocol[protocol.key] || []).forEach((modelOption) => {
        const model = String(modelOption || "").trim();
        const normalizedModel = normalizeProtocolValue(model);
        if (!model || !normalizedModel) return;

        const nextOption = {
          label: model,
          value: model,
          protocolKey: protocol.key,
          protocolLabel: protocol.label,
        };
        const existing = rowsByModel.get(normalizedModel);

        // A model may be supported by more than one parser. For the current
        // device, retain its stored routing protocol; otherwise use the first
        // registered option returned by the API.
        if (!existing || protocol.key === currentProtocolKey) {
          rowsByModel.set(normalizedModel, nextOption);
        }
      });
    });

    const rows = Array.from(rowsByModel.values());
    if (
      form.model &&
      !rows.some(
        (row) => normalizeProtocolValue(row.value) === normalizeProtocolValue(form.model)
      )
    ) {
      rows.unshift({
        label: form.model,
        value: form.model,
        protocolKey: currentProtocolKey,
        protocolLabel:
          protocolOptions.find((protocol) => protocol.key === currentProtocolKey)?.label ||
          currentProtocolKey,
      });
    }
    return rows;
  }, [
    protocolOptions,
    modelOptionsByProtocol,
    currentProtocolKey,
    form.model,
  ]);
  const isSinotrackSelected = currentProtocolKey === "sinotrack";
  const showCameraSetupSection = currentProtocolKey === "id0024g";
  const canCurrentActorCreateUserRole = useMemo(
    () => ["admin", "hr", "office_emp", "sub_admin", "group_admin"].includes(currentUserRole),
    [currentUserRole]
  );
  const addNewUserOption = useMemo(
    () => ({
      label: "Add New User",
      value: INLINE_ADD_USER_OPTION_VALUE,
      isAction: true,
    }),
    []
  );
  const selectedDaysLeft = useMemo(() => {
    const now = new Date();
    const ms = expiryDate.getTime() - now.getTime();
    return Math.ceil(ms / (24 * 60 * 60 * 1000));
  }, [expiryDate]);

  const styles = {
    dialogPaper: {
      borderRadius: isMobile ? 0 : 4,
      overflow: "hidden",
      height: isMobile ? "100vh" : "min(88vh, 760px)",
      width: isMobile ? "100%" : "min(92vw, 820px)",
      maxWidth: "none",
      display: "flex",
      flexDirection: "column",
      boxShadow: `0 28px 90px ${alpha(theme.palette.common.black, 0.26)}`,
    },
    stickyHeader: {
      position: "sticky",
      top: 0,
      zIndex: 5,
      bgcolor: "background.paper",
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
      boxShadow: "none",
    },
    stickyFooter: {
      position: "sticky",
      bottom: 0,
      zIndex: 5,
      bgcolor: "background.paper",
      borderTop: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    },
  };

  const wizardSteps = [
    {
      title: "Device Information",
      description: "Basic device & user details",
      icon: <MemoryIcon fontSize="small" />,
    },
    {
      title: "Extra Information",
      description: "Model, SIM & optional info",
      icon: <GroupWorkIcon fontSize="small" />,
    },
    {
      title: "Advanced Settings",
      description: "Tracking preferences",
      icon: <RouteIcon fontSize="small" />,
    },
  ];

  const goNextStep = () => setActiveStep((step) => Math.min(step + 1, wizardSteps.length - 1));
  const goPreviousStep = () => setActiveStep((step) => Math.max(step - 1, 0));

  const toggleSection = (key) => (_, nextExpanded) => {
    setExpandedSections((prev) => ({ ...prev, [key]: nextExpanded }));
  };

  const iconOptions = useMemo(
    () =>
      ICONS.map((icon) => ({
        label: icon
          .split("_")
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(" "),
        value: icon,
      })),
    []
  );
  const wideFieldSx = useMemo(
    () => ({
      minWidth: 0,
      width: "100%",
      "& .MuiInputBase-root": {
        minHeight: 48,
      },
      "& .MuiAutocomplete-input": {
        minWidth: "0 !important",
      },
      "& .MuiInputBase-input": {
        textOverflow: "clip",
      },
    }),
    []
  );

  useEffect(() => {
    if (!form.assignedToUser) return;
    if (scopedUserIds.has(String(form.assignedToUser))) return;
    setField("assignedToUser", "");
  }, [form.dealer, form.admin, form.assignedToUser, scopedUserIds, setField]);

  useEffect(() => {
    if (!form.sinotrackUseImeiAsVehicleId) return;
    setForm((prev) => {
      const nextVehicleId = String(prev.imei || "").trim();
      if (prev.sinotrackVehicleId === nextVehicleId) return prev;
      return {
        ...prev,
        sinotrackVehicleId: nextVehicleId,
      };
    });
  }, [form.imei, form.sinotrackUseImeiAsVehicleId]);

  useEffect(() => {
    if (!form.admin || !form.dealer) return;
    const dealerStillVisible = dealerOptions.some(
      (option) => String(option.value) === String(form.dealer)
    );
    if (dealerStillVisible) return;

    setForm((prev) => ({
      ...prev,
      dealer: "",
      assignedToUser: "",
    }));
    setErrors((prev) => ({
      ...prev,
      dealer: undefined,
      assignedToUser: undefined,
    }));
  }, [form.admin, form.dealer, dealerOptions]);

  const openInlineUserCreate = useCallback(() => {
    if (!canCurrentActorCreateUserRole) {
      window.alert("Aapke role ko direct user create karne ki permission nahi hai.");
      return;
    }

    setShowInlineAddUserModal(true);
  }, [canCurrentActorCreateUserRole]);

  const handleInlineUserCreated = useCallback(
    async (result) => {
      const createdUser = result?.user || result?.data?.user || result?.data || result;
      const createdUserId = resolveEntityId(createdUser);

      if (createdUserId) {
        setInlineCreatedUsers((prev) => {
          const next = prev.filter((user) => resolveEntityId(user) !== createdUserId);
          next.unshift(createdUser);
          return next;
        });
        setField("assignedToUser", createdUserId);
      }

      try {
        await refetchAllUsers?.();
      } catch (error) {
        console.warn("User list refresh failed after inline create", error);
      }

      setShowInlineAddUserModal(false);
    },
    [refetchAllUsers, setField]
  );

  return (
    <Dialog
      open={open}
      onClose={() => (isSubmitting ? null : onClose?.())}
      fullWidth
      maxWidth={false}
      fullScreen={isMobile}
      PaperProps={{ sx: styles.dialogPaper }}
    >
      {/* HEADER */}
      <DialogTitle sx={{ ...styles.stickyHeader, position: "relative", px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
        <Stack
          direction="column"
          alignItems="stretch"
          justifyContent="space-between"
          spacing={1.5}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, pr: 7 }}>
            <Box
              sx={(t) => ({
                width: 46,
                height: 46,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                color: t.palette.primary.main,
                bgcolor: alpha(t.palette.primary.main, 0.1),
                boxShadow: `0 16px 34px ${alpha(t.palette.primary.main, 0.18)}`,
              })}
            >
              <MemoryIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={900} noWrap sx={{ letterSpacing: "-0.02em" }}>
                {isEditMode ? "Edit Device" : "Add New Device"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Add and configure your tracking device
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            sx={{ width: "100%" }}
          >
            {wizardSteps.map((step, index) => {
              const completed = index < activeStep;
              const current = index === activeStep;
              return (
                <Box
                  key={step.title}
                  component="button"
                  type="button"
                  onClick={() => setActiveStep(index)}
                  sx={(t) => ({
                    flex: 1,
                    border: 0,
                    cursor: "pointer",
                    textAlign: "left",
                    p: 1,
                    borderRadius: 2,
                    bgcolor: current ? alpha(t.palette.primary.main, 0.08) : "transparent",
                    color: current ? t.palette.primary.main : t.palette.text.secondary,
                    borderBottom: current ? `3px solid ${t.palette.primary.main}` : "3px solid transparent",
                    transition: "all 180ms ease",
                  })}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={(t) => ({
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        fontWeight: 900,
                        fontSize: 13,
                        color: completed || current ? "#fff" : t.palette.text.secondary,
                        bgcolor: completed
                          ? t.palette.success.main
                          : current
                            ? t.palette.primary.main
                            : alpha(t.palette.text.primary, 0.08),
                      })}
                    >
                      {completed ? "✓" : index + 1}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 900, lineHeight: 1.15 }}>
                        {step.title}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }} noWrap>
                        {step.description}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>

          <IconButton
            onClick={onClose}
            disabled={isSubmitting}
            sx={(t) => ({
              alignSelf: { xs: "flex-end", lg: "center" },
              position: "absolute",
              right: { xs: 16, sm: 24 },
              top: { xs: 16, sm: 22 },
              width: 42,
              height: 42,
              border: `1px solid ${alpha(t.palette.divider, 0.9)}`,
              bgcolor: alpha(t.palette.text.primary, 0.03),
            })}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        {/* CONTENT */}
        <DialogContent
          dividers
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            p: { xs: 2, sm: 3.5 },
            bgcolor: "#f5f8ff",
          "& .MuiTextField-root .MuiInputBase-root": {
              width: "100%",
              minHeight: 48,
              borderRadius: 2,
              bgcolor: "#fff",
            },
            "& .MuiInputLabel-root": {
              fontWeight: 800,
              color: "#334155",
            },
            "& .MuiFormHelperText-root": {
              fontWeight: 600,
            },
            "& .MuiButton-root": {
              textTransform: "none",
            },
          }}
        >
          {/* banners */}
          <Stack spacing={1.25} sx={{ mb: 2 }}>
            {anyLoading && (
              <Alert severity="info" icon={<CircularProgress size={18} />}>
                Loading dropdowns…
              </Alert>
            )}
            {isEditMode && fetchingDeviceDetails && (
              <Alert severity="info" icon={<CircularProgress size={18} />}>
                Loading latest device details for edit…
              </Alert>
            )}
            {anyError && (
              <Alert severity="warning">
                Some dropdowns failed to load. You can still type values manually where possible.
              </Alert>
            )}
            {apiError && <Alert severity="error">{apiError}</Alert>}
          </Stack>

          <Grid container spacing={0} alignItems="stretch" sx={{ width: "100%", maxWidth: "100%", m: 0 }}>
            <Grid item xs={12} sx={{ width: "100%", maxWidth: "100%", flexBasis: "100%", minWidth: 0 }}>
              <Stack spacing={2.25} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, alignItems: "stretch" }}>
                {activeStep === 0 && (
            <CollapsibleSection
              title="Device details"
              subtitle="Main identification and assignment details"
              icon={<MemoryIcon fontSize="small" />}
              expanded={expandedSections.details}
              onChange={toggleSection("details")}
              defaultExpanded
            >
                  <Grid container spacing={2} className="register-device-form-grid">
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Registration No *"
                        value={form.registrationNo}
                        onChange={(e) => setField("registrationNo", e.target.value)}
                        placeholder="e.g. MH12AB1234"
                        fullWidth
                        error={Boolean(errors.registrationNo)}
                        helperText={errors.registrationNo || "Vehicle / asset registration number"}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <RouteIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="IMEI"
                        value={form.imei}
                        onChange={(e) => setField("imei", e.target.value.replace(/\D/g, ""))}
                        placeholder="Enter IMEI"
                        inputProps={{ inputMode: "numeric" }}
                        fullWidth
                        error={Boolean(errors.imei)}
                        helperText={
                          errors.imei ||
                          (String(form.imei || "").trim()
                            ? `${String(form.imei || "").length} digits entered`
                            : "Optional for all protocols")
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <NumbersIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    {currentUserRole === "admin" && (
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          PopperComponent={WideAutocompletePopper}
                          options={subAdminOptions}
                          value={subAdminOptions.find((o) => o.value === form.admin) || null}
                          filterOptions={ownerAutocompleteFilter}
                          sx={wideFieldSx}
                          getOptionLabel={(option) => option?.label || ""}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} sx={{ alignItems: "flex-start", py: 1 }}>
                              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: "normal" }}>
                                  {option.meta?.username || option.meta?.name || option.label}
                                </Typography>
                                {option.secondaryLabel ? (
                                  <Typography variant="caption" color="text.secondary">
                                    {option.secondaryLabel}
                                  </Typography>
                                ) : null}
                              </Stack>
                            </Box>
                          )}
                          onChange={(_, v) => {
                            const nextAdmin = v?.value || "";
                            setForm((prev) => ({
                              ...prev,
                              admin: nextAdmin,
                              dealer:
                                nextAdmin && String(nextAdmin) === String(prev.admin)
                                  ? prev.dealer
                                  : "",
                              assignedToUser: "",
                            }));
                            setErrors((prev) => ({
                              ...prev,
                              admin: undefined,
                              dealer: undefined,
                              assignedToUser: undefined,
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Admin"
                              fullWidth
                              helperText={allUsersLoading ? "Loading admins…" : "Select sub admin"}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <GroupWorkIcon fontSize="small" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        PopperComponent={WideAutocompletePopper}
                        options={dealerOptions}
                        value={dealerOptions.find((o) => o.value === form.dealer) || null}
                        filterOptions={ownerAutocompleteFilter}
                        sx={wideFieldSx}
                        getOptionLabel={(option) => option?.label || ""}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} sx={{ alignItems: "flex-start", py: 1 }}>
                            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: "normal" }}>
                                {option.meta?.username || option.meta?.name || option.label}
                              </Typography>
                              {option.secondaryLabel ? (
                                <Typography variant="caption" color="text.secondary">
                                  {option.secondaryLabel}
                                </Typography>
                              ) : null}
                            </Stack>
                          </Box>
                        )}
                        onChange={(_, v) => {
                          const nextDealer = v?.value || "";
                          const nextDealerOption = dealerOptions.find(
                            (option) => String(option.value) === String(nextDealer)
                          );

                          if (
                            nextDealerOption &&
                            String(nextDealer) !== String(form.dealer) &&
                            nextDealerOption.hasKnownLicenseCount &&
                            Number(nextDealerOption.remainingLicenses || 0) <= 0
                          ) {
                            setDealerLicenseDialog({
                              open: true,
                              dealerName: nextDealerOption.label || "Dealer",
                            });
                            return;
                          }

                          setForm((prev) => ({
                            ...prev,
                            dealer: nextDealer,
                            assignedToUser:
                              nextDealer && String(nextDealer) === String(prev.dealer)
                                ? prev.assignedToUser
                                : "",
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            dealer: undefined,
                            assignedToUser: undefined,
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Dealer"
                            fullWidth
                            error={Boolean(errors.dealer)}
                            helperText={dealersLoading ? "Loading dealers…" : "Select"}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <StoreIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        PopperComponent={WideAutocompletePopper}
                        options={userOptions}
                        value={userOptions.find((o) => o.value === form.assignedToUser) || null}
                        sx={wideFieldSx}
                        filterOptions={(options, params) => {
                          const filtered = ownerAutocompleteFilter(options, params);
                          const hasExactMatch = filtered.some(
                            (option) => !option.isAction && option.label === params.inputValue
                          );

                          if (!filtered.length || (!hasExactMatch && !params.inputValue.trim() && !options.length)) {
                            filtered.push(addNewUserOption);
                          }

                          return filtered;
                        }}
                        getOptionLabel={(option) => option?.label || ""}
                        renderOption={(props, option) => (
                          option.isAction ? (
                            <Box component="li" {...props} sx={{ alignItems: "center", py: 1.25 }}>
                              <Typography variant="body2" fontWeight={800} color="primary.main">
                                {option.label}
                              </Typography>
                            </Box>
                          ) : (
                            <Box component="li" {...props} sx={{ alignItems: "flex-start", py: 1 }}>
                              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: "normal" }}>
                                  {option.meta?.username || option.meta?.name || option.label}
                                </Typography>
                                {option.secondaryLabel ? (
                                  <Typography variant="caption" color="text.secondary">
                                    {option.secondaryLabel}
                                  </Typography>
                                ) : null}
                              </Stack>
                            </Box>
                          )
                        )}
                        onChange={(_, v) => {
                          if (v?.isAction || v?.value === INLINE_ADD_USER_OPTION_VALUE) {
                            openInlineUserCreate();
                            return;
                          }
                          setField("assignedToUser", v?.value || "");
                        }}
                        noOptionsText="Add New User"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="User"
                            fullWidth
                            helperText={
                              usersLoading || allUsersLoading
                                ? "Loading users…"
                                : form.dealer
                                  ? userOptions.length
                                    ? "Dealer ke under wale users"
                                    : "Selected dealer ke under koi user nahi mila. Add New User use karo."
                                  : form.admin
                                    ? userOptions.length
                                      ? "Selected admin hierarchy ke users"
                                      : "Selected admin ke under koi matching user nahi mila."
                                  : "Select"
                            }
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  {requiresDealerLicenseBinding && (
                    <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                      {canUseSelfLicenseCount
                        ? `Your role (${currentUserRole}) can use its own license count or a dealer license.`
                        : `Your role (${currentUserRole}) requires dealer or dealer-linked assignment before saving.`}
                    </Alert>
                  )}
            </CollapsibleSection>
                )}

                {activeStep === 1 && (
            <CollapsibleSection
              title="Extra"
              subtitle="Optional details, license and camera fields"
              icon={<GroupWorkIcon fontSize="small" />}
              expanded={expandedSections.extra}
              onChange={toggleSection("extra")}
            >
                  <Grid container spacing={2} className="register-device-form-grid">
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        options={groupOptions}
                        value={groupOptions.find((o) => o.value === form.deviceGroup) || null}
                        onChange={(_, v) => setField("deviceGroup", v?.value || "")}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Group"
                            fullWidth
                            helperText={groupsLoading ? "Loading groups…" : "Select group"}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Sim number 1"
                        value={form.simNumber}
                        onChange={(e) => setField("simNumber", e.target.value.replace(/\D/g, ""))}
                        fullWidth
                        error={Boolean(errors.simNumber)}
                        helperText={errors.simNumber || "Optional device SIM"}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Sim number 2"
                        value={form.simNumber2}
                        onChange={(e) => setField("simNumber2", e.target.value.replace(/\D/g, ""))}
                        fullWidth
                        error={Boolean(errors.simNumber2)}
                        helperText={errors.simNumber2 || "Optional secondary SIM"}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        PopperComponent={WideAutocompletePopper}
                        options={protocolModelOptionsList}
                        value={
                          protocolModelOptionsList.find(
                            (option) =>
                              normalizeProtocolValue(option.value) ===
                              normalizeProtocolValue(form.model)
                          ) || null
                        }
                        sx={wideFieldSx}
                        onChange={(_, v) => {
                          const nextProtocol = v?.protocolKey || "";
                          const nextModel = v?.value || "";
                          setForm((prev) => ({
                            ...prev,
                            protocolKey: nextProtocol,
                            model: nextModel,
                            sinotrackManufacturerCode:
                              nextProtocol === "sinotrack"
                                ? prev.sinotrackManufacturerCode || DEFAULT_SINOTRACK_MANUFACTURER_CODE
                                : prev.sinotrackManufacturerCode,
                            sinotrackVehicleId:
                              nextProtocol === "sinotrack" && prev.sinotrackUseImeiAsVehicleId
                                ? String(prev.imei || "").trim()
                                : prev.sinotrackVehicleId,
                          }));
                          setErrors((prev) => ({ ...prev, protocolKey: undefined, model: undefined }));
                        }}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} sx={{ py: 1 }}>
                            <Box>
                              <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: "normal" }}>
                                {option.label}
                              </Typography>
                              {option.protocolLabel ? (
                                <Typography variant="caption" color="text.secondary">
                                  {option.protocolLabel}
                                </Typography>
                              ) : null}
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Protocol *"
                            fullWidth
                            error={Boolean(errors.protocolKey || errors.model)}
                            helperText={
                              errors.protocolKey ||
                              errors.model ||
                              (modelsLoading ? "Loading protocols…" : "Select protocol model")
                            }
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Driver no"
                        value={form.driverNumber}
                        onChange={(e) => setField("driverNumber", e.target.value)}
                        fullWidth
                        helperText="Driver contact number"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        options={iconOptions}
                        value={iconOptions.find((o) => o.value === form.deviceIcon) || null}
                        onChange={(_, v) => setField("deviceIcon", v?.value || "car")}
                        renderInput={(params) => (
                          <TextField {...params} label="Icon" fullWidth helperText="Map marker icon" />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="License Expiration"
                        type="date"
                        value={form.licenseExpiryDate}
                        onChange={(e) => setField("licenseExpiryDate", e.target.value)}
                        fullWidth
                        error={Boolean(errors.licenseExpiryDate)}
                        InputLabelProps={{ shrink: true }}
                        helperText={errors.licenseExpiryDate || "Select license expiry date"}
                      />
                    </Grid>

                    {isEditMode && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Todays Odo (mtr)"
                          type="number"
                          value={form.todaysOdo}
                          onChange={(e) => setField("todaysOdo", e.target.value)}
                          fullWidth
                          inputProps={{ min: 0, step: 0.01 }}
                          helperText="Edit mode only"
                        />
                      </Grid>
                    )}
                  </Grid>

                  {isSinotrackSelected && (
                    <Paper
                      variant="outlined"
                      sx={{
                        mt: 2.5,
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 2.5,
                        borderColor: alpha(theme.palette.warning.main, 0.35),
                        bgcolor: alpha(theme.palette.warning.light, 0.08),
                      }}
                    >
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={900}>
                            SinoTrack Settings
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Configure how the SinoTrack packet vehicle ID maps to this device.
                          </Typography>
                        </Box>

                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(form.sinotrackUseImeiAsVehicleId)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setForm((prev) => ({
                                  ...prev,
                                  sinotrackUseImeiAsVehicleId: checked,
                                  sinotrackVehicleId: checked
                                    ? String(prev.imei || "").trim()
                                    : prev.sinotrackVehicleId,
                                }));
                                setErrors((prev) => ({ ...prev, sinotrackVehicleId: undefined }));
                              }}
                            />
                          }
                          label="Use IMEI as Vehicle ID"
                        />

                        <Grid container spacing={2} className="register-device-form-grid">
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="SinoTrack Vehicle ID"
                              value={
                                form.sinotrackUseImeiAsVehicleId
                                  ? String(form.imei || "").trim()
                                  : form.sinotrackVehicleId
                              }
                              onChange={(e) => setField("sinotrackVehicleId", e.target.value)}
                              fullWidth
                              disabled={Boolean(form.sinotrackUseImeiAsVehicleId)}
                              required={!form.sinotrackUseImeiAsVehicleId}
                              error={Boolean(errors.sinotrackVehicleId)}
                              helperText={
                                errors.sinotrackVehicleId ||
                                (form.sinotrackUseImeiAsVehicleId
                                  ? "Vehicle ID will follow the IMEI"
                                  : "Required for SinoTrack when IMEI mapping is disabled")
                              }
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Manufacturer Code"
                              value={form.sinotrackManufacturerCode}
                              onChange={(e) =>
                                setField(
                                  "sinotrackManufacturerCode",
                                  e.target.value.toUpperCase()
                                )
                              }
                              fullWidth
                              helperText="Defaults to TH"
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Device Password (optional)"
                              value={form.sinotrackDevicePassword}
                              onChange={(e) => setField("sinotrackDevicePassword", e.target.value)}
                              fullWidth
                              helperText="Optional password for device-side SinoTrack commands"
                            />
                          </Grid>
                        </Grid>
                      </Stack>
                    </Paper>
                  )}
                  {selectedDaysLeft <= 7 && (
                    <Alert severity={selectedDaysLeft <= 0 ? "error" : "warning"} sx={{ mt: 2, borderRadius: 2 }}>
                      {selectedDaysLeft <= 0
                        ? "Selected validity is expiring now. Please increase validity."
                        : `Your expiration days left: ${selectedDaysLeft}`}
                    </Alert>
                  )}

                  {showCameraSetupSection && (
                    <Stack spacing={2} sx={{ mt: 2.5 }}>
                      <TextField
                        label="Terminal ID"
                        value={form.terminalId}
                        onChange={(e) => setField("terminalId", e.target.value)}
                        placeholder="Camera/JT808 terminal id"
                        fullWidth
                        error={Boolean(errors.terminalId)}
                        helperText={
                          errors.terminalId ||
                          (isEditMode
                            ? "JT808 device terminal id"
                            : "Auto camera register ke liye Terminal ID required hai")
                        }
                      />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(form.autoRegisterCamera)}
                            onChange={(e) => setField("autoRegisterCamera", e.target.checked)}
                            disabled={isEditMode}
                          />
                        }
                        label="Auto register camera node after device create"
                      />

                      <Collapse in={Boolean(form.autoRegisterCamera) && !isEditMode}>
                        <Grid container spacing={2} className="register-device-form-grid">
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Camera Node Name"
                              value={form.cameraNodeName}
                              onChange={(e) => setField("cameraNodeName", e.target.value)}
                              placeholder="e.g. Front Dash Camera"
                              fullWidth
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Channel"
                              type="number"
                              value={form.cameraChannelId}
                              onChange={(e) => setField("cameraChannelId", e.target.value)}
                              fullWidth
                              inputProps={{ min: 1, max: 255 }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Stream Type"
                              select
                              value={form.cameraStreamType}
                              onChange={(e) => setField("cameraStreamType", e.target.value)}
                              fullWidth
                            >
                              <MenuItem value={0}>Main Stream (0)</MenuItem>
                              <MenuItem value={1}>Sub Stream (1)</MenuItem>
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Data Type"
                              select
                              value={form.cameraDataType}
                              onChange={(e) => setField("cameraDataType", e.target.value)}
                              fullWidth
                            >
                              <MenuItem value={1}>Video (1)</MenuItem>
                              <MenuItem value={0}>Audio + Video (0)</MenuItem>
                            </TextField>
                          </Grid>
                        </Grid>
                      </Collapse>
                    </Stack>
                  )}
            </CollapsibleSection>
                )}

                {activeStep === 2 && (
            <CollapsibleSection
              title="Data Push"
              subtitle={isDataPushEnabled ? "Enabled for this device" : "Toggle device data push delivery"}
              icon={<RouteIcon fontSize="small" />}
              expanded={expandedSections.dataPush}
              onChange={toggleSection("dataPush")}
            >
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 2.5,
                  borderColor: alpha(theme.palette.divider, 0.9),
                  bgcolor: alpha(theme.palette.background.paper, 0.85),
                }}
              >
                <Stack spacing={1.25}>
                  <FormControlLabel
                    sx={{ m: 0, alignItems: "flex-start" }}
                    control={
                      <Switch
                        checked={Boolean(isDataPushEnabled)}
                        onChange={(e) => setDataPushEnabled(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle2" fontWeight={800}>
                          Enable Data Push
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Register device par enable karoge to wahi setting edit device mein bhi same live dikhegi.
                        </Typography>
                      </Box>
                    }
                  />

                  <Typography variant="caption" color="text.secondary">
                    {isDataPushEnabled
                      ? "Data push is active and will be saved with this device."
                      : "Data push is currently off for this device."}
                  </Typography>
                </Stack>
              </Paper>
            </CollapsibleSection>
                )}

                {activeStep === 2 && (
            <CollapsibleSection
              title="Advance Settings"
              subtitle={`${advancedCustomFields.length} attributes added`}
              icon={<InfoOutlinedIcon fontSize="small" />}
              expanded={expandedSections.advanced}
              onChange={toggleSection("advanced")}
            >
                  {errors.customFields && (
                    <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>
                      {errors.customFields}
                    </Alert>
                  )}

                  <Stack spacing={1.25}>
                    {advancedCustomFields.map(({ field: row, sourceIndex }) => (
                      <Paper
                        key={`${row.key || row.fieldName}-${sourceIndex}`}
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          borderRadius: 2.5,
                          borderColor: alpha(theme.palette.divider, 0.9),
                          bgcolor: alpha(theme.palette.background.paper, 0.85),
                        }}
                      >
                        <Grid container spacing={1.25} alignItems="center">
                          <Grid item xs={12} md={4}>
                            {row.fieldType === "boolean" ? (
                              <FormControlLabel
                                sx={{ minHeight: 56, m: 0 }}
                                control={
                                  <Switch
                                    checked={row.fieldValue === true || row.fieldValue === "true"}
                                    onChange={(e) =>
                                      setCustomField(sourceIndex, { fieldValue: e.target.checked ? "true" : "false" })
                                    }
                                  />
                                }
                                label={row.fieldName}
                              />
                            ) : (
                              <TextField
                                label={row.fieldName}
                                value={row.fieldValue}
                                onChange={(e) => setCustomField(sourceIndex, { fieldValue: e.target.value })}
                                fullWidth
                                placeholder={`Enter ${row.fieldName}`}
                                type={row.fieldType === "number" ? "number" : row.fieldType === "date" ? "date" : "text"}
                                InputLabelProps={row.fieldType === "date" ? { shrink: true } : undefined}
                              />
                            )}
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <TextField
                              select
                              label="Type"
                              value={row.fieldType || "text"}
                              onChange={() => {}}
                              fullWidth
                              disabled
                            >
                              {FIELD_TYPES.map((t) => (
                                <MenuItem key={t.value} value={t.value}>
                                  {t.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <TextField
                              label="Stored Value"
                              value={row.fieldType === "boolean" ? (row.fieldValue === "true" || row.fieldValue === true ? "Yes" : "No") : row.fieldValue}
                              fullWidth
                              disabled
                            />
                          </Grid>

                          <Grid item xs={12} md={1}>
                            <Stack direction="row" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                              <Tooltip title={row.required ? "Marked as required" : "Optional value"}>
                                <IconButton
                                  size="small"
                                  onClick={() => setCustomField(sourceIndex, { required: !row.required })}
                                  sx={{
                                    bgcolor: alpha(theme.palette.primary.main, row.required ? 0.12 : 0.04),
                                    mr: 0.5,
                                  }}
                                >
                                  <InfoOutlinedIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>

                              <IconButton
                                size="small"
                                onClick={() => removeCustomRow(sourceIndex)}
                                color="error"
                                disabled={isSubmitting && advancedCustomFields.length === 1}
                                sx={{ bgcolor: alpha(theme.palette.error.main, 0.06) }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              {row.required ? "Required: this field must have a value." : "Optional field."}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}

                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        borderRadius: 2.5,
                        borderColor: alpha(theme.palette.divider, 0.9),
                        bgcolor: alpha(theme.palette.background.paper, 0.85),
                        width: "100%",
                        maxWidth: 760,
                        mx: "auto",
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Autocomplete
                          PopperComponent={WideAutocompletePopper}
                          options={availableAttributeOptions}
                          value={
                            availableAttributeOptions.find(
                              (item) => item.key === form.pendingAttributeKey
                            ) || null
                          }
                          onChange={(_, value) => setField("pendingAttributeKey", value?.key || "")}
                          filterOptions={attributeAutocompleteFilter}
                          getOptionLabel={(option) => option?.fieldName || ""}
                          ListboxProps={{
                            style: {
                              maxHeight: 280,
                              overflowY: "auto",
                            },
                          }}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} sx={{ py: 1 }}>
                              <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: "normal" }}>
                                {option.fieldName}
                              </Typography>
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Attribute"
                              placeholder="Search attribute"
                              fullWidth
                              sx={{
                                "& .MuiInputBase-root": {
                                  minHeight: 48,
                                },
                              }}
                            />
                          )}
                        />

                        <TextField
                          label="Type"
                          value={
                            DEVICE_ATTRIBUTE_DEFS.find((item) => item.key === form.pendingAttributeKey)?.fieldType || ""
                          }
                          fullWidth
                          disabled
                          sx={{
                            "& .MuiInputBase-root": {
                              minHeight: 48,
                            },
                          }}
                        />

                        <Button
                          type="button"
                          onClick={addCustomRow}
                          startIcon={<AddIcon />}
                          variant="outlined"
                          disabled={!form.pendingAttributeKey}
                          fullWidth
                          sx={{
                            borderRadius: 1.5,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            minHeight: 46,
                            justifyContent: "center",
                          }}
                        >
                          Add
                        </Button>
                      </Stack>
                    </Paper>
                  </Stack>
            </CollapsibleSection>
                )}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>

        {/* FOOTER */}
        <DialogActions sx={{ ...styles.stickyFooter, px: { xs: 2, sm: 3 }, py: 2 }}>
          <Box sx={{ width: "100%", display: "flex", gap: 1.5, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center", fontWeight: 800 }}>
              Step {activeStep + 1} of {wizardSteps.length} · Required: Registration No, IMEI, Model
            </Typography>

            <Stack direction="row" spacing={1.25} sx={{ ml: "auto" }}>
              {activeStep === 0 ? (
                <Button
                  onClick={onClose}
                  disabled={isSubmitting}
                  variant="outlined"
                  sx={{ borderRadius: 2.5, fontWeight: 900, minWidth: 110, minHeight: 44 }}
                >
                  Cancel
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={goPreviousStep}
                  disabled={isSubmitting}
                  variant="outlined"
                  sx={{ borderRadius: 2.5, fontWeight: 900, minWidth: 110, minHeight: 44 }}
                >
                  Back
                </Button>
              )}

              {activeStep < wizardSteps.length - 1 ? (
                <Button
                  type="button"
                  variant="contained"
                  onClick={goNextStep}
                  disabled={isSubmitting}
                  sx={{
                    borderRadius: 2.5,
                    fontWeight: 900,
                    minWidth: 140,
                    minHeight: 44,
                    boxShadow: `0 14px 30px ${alpha(theme.palette.primary.main, 0.28)}`,
                  }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
                  sx={{
                    borderRadius: 2.5,
                    fontWeight: 900,
                    minWidth: 160,
                    minHeight: 44,
                    bgcolor: "#2563eb",
                    boxShadow: "0 14px 34px rgba(37,99,235,0.28)",
                    "&:hover": { bgcolor: "#1d4ed8" },
                  }}
                >
                  {isEditMode ? (updating ? "Updating…" : "Update Device") : creating ? "Saving…" : "Save Device"}
                </Button>
              )}
            </Stack>
          </Box>
        </DialogActions>
      </form>

      <AddUserModal
        open={showInlineAddUserModal}
        onClose={() => setShowInlineAddUserModal(false)}
        onSuccess={handleInlineUserCreated}
        mode="create"
      />

      <Dialog
        open={dealerLicenseDialog.open}
        onClose={() =>
          setDealerLicenseDialog({
            open: false,
            dealerName: "",
          })
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 4, pb: 2 }}>
          <Stack spacing={2.25} alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 92,
                height: 92,
                borderRadius: "50%",
                border: `4px solid ${alpha(theme.palette.error.main, 0.35)}`,
                color: "error.main",
                display: "grid",
                placeItems: "center",
                fontSize: 54,
                lineHeight: 1,
                fontWeight: 300,
              }}
            >
              ×
            </Box>
            <Typography variant="h5" fontWeight={900} sx={{ maxWidth: 420 }}>
              {dealerLicenseDialog.dealerName || "Dealer"} does not have enough
              license to add the device
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3, pt: 0 }}>
          <Button
            variant="contained"
            onClick={() =>
              setDealerLicenseDialog({
                open: false,
                dealerName: "",
              })
            }
            sx={{ minWidth: 120, fontWeight: 800 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
